<template>
  <Transition name="list-slide">
    <div v-if="isOpen" :class="['locations-list-panel', { 'is-mobile': isMobile }]">
      <div class="list-header">
        <div class="list-section-label">{{ localize('locations.common.nearby') }}</div>
        <div class="list-tabs">
          <div :class="['list-tab', { active: activeTab === 'boards' }]" @click="activeTab = 'boards'">
            <GamIcon :name="GamIconName.GAMBIT" size="28px" :is-primary="activeTab === 'boards'" />
            <h5>{{ localize('location.list.tab.boards') }}</h5>
          </div>
          <div :class="['list-tab', { active: activeTab === 'irl' }]" @click="activeTab = 'irl'">
            <GamIcon :name="GamIconName.PLAY_EMPTY" size="28px" :is-primary="activeTab === 'irl'" />
            <h5>{{ localize('location.list.tab.irl') }}</h5>
          </div>
        </div>
      </div>

      <div class="list-scroll">
        <div v-if="tabLocations.length" class="circles-grid">
          <LocationCircle
            v-for="location in tabLocations"
            :key="location.id"
            :location="location"
            :mode="activeTab"
            :selected="selectedLocationId === location.id"
            @click="updateSelectedLocation"
            @click-board="updateSelectedLocation"
          />
        </div>
        <div v-else-if="isLoading" class="skeleton-loader">
          <div class="skeleton-item skeleton-circle" />
          <div class="skeleton-item skeleton-circle" />
          <div class="skeleton-item skeleton-circle" />
          <div class="skeleton-item skeleton-circle" />
          <div class="skeleton-item skeleton-circle" />
          <div class="skeleton-item skeleton-circle" />
        </div>
        <div v-else class="empty-state">
          <GamIcon
            :name="activeTab === 'boards' ? GamIconName.GAMBIT : GamIconName.PLAY_EMPTY"
            size="28px"
            class="empty-state-icon"
          />
          <span class="empty-state-label">{{
            localize(activeTab === 'boards' ? 'location.list.empty.boards' : 'location.list.empty.irl')
          }}</span>
        </div>

        <div v-if="currentZoom > 12" class="zoom-out-cta" @click="handleZoomOut">
          <span>{{ localize('location.list.zoom_out') }}</span>
        </div>
      </div>

      <div class="list-close-btn" @click="closeListView">
        <GamIcon :name="GamIconName.X_CLOSE" :size="GamIconSize.X_LARGE" is-current />
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import type { BaseLocationDto } from '@/core/data/location/location.interface';
import { LocationType } from '@/core/data/location/location.type';
import { gambits, localize } from '@/core/gambits';
import { isMobile } from '@/core/helpers/ui.helper';
import { DEFAULT_RADIUS, useMapStore } from '@/stores/map.store';
import { useTableStore } from '@/stores/table.store';
import LocationCircle from '@/views/components/gam-location/LocationCircle.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import { GamListId } from '@/views/composables/constants/components/gamIntersect.constants';
import { debounce } from 'lodash';
import { storeToRefs } from 'pinia';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const listId = GamListId.LOCATIONS;

const mapStore = useMapStore();
const { currentRadius, currentZoom } = storeToRefs(mapStore);
const tableStore = useTableStore<BaseLocationDto>(listId)();
const { itemList, isLoading } = storeToRefs(tableStore);

const router = useRouter();
const route = useRoute();

const loadGeneration = ref(0);
const selectedLocationId = ref<string | null>(null);

const deduplicatedItemList = computed(() => {
  if (!itemList.value) return null;
  const seen = new Set<string>();
  return itemList.value.filter((location) => {
    if (seen.has(location.id)) return false;
    seen.add(location.id);
    return true;
  });
});

const filteredLocations = computed(() => {
  if (!deduplicatedItemList.value) return [];
  return deduplicatedItemList.value.filter((loc) => loc.type !== LocationType.CITY);
});

const activeTab = ref<'boards' | 'irl'>('boards');

const tabLocations = computed(() => {
  if (!filteredLocations.value.length) return [];
  if (activeTab.value === 'boards') {
    return filteredLocations.value.filter((loc) => (loc.activeChessboardCount ?? 0) > 0);
  }
  return filteredLocations.value.filter((loc) => (loc.visitors?.length ?? 0) > 0 || (loc.nextGambits?.length ?? 0) > 0);
});

const isOpen = computed(() => {
  return route.query.side_panel === 'search';
});

const loadLocationsForMapCenter = debounce(async () => {
  if (!route.query.lat || !route.query.lng) {
    return;
  }

  const location = {
    lat: parseFloat(String(route.query.lat)),
    lng: parseFloat(String(route.query.lng)),
  };

  const generation = ++loadGeneration.value;
  tableStore.setCursor(null);
  const items = await gambits.locationService.getLocations(listId, location, currentRadius.value || DEFAULT_RADIUS);

  if (generation !== loadGeneration.value) return;

  if (!items.data) {
    return;
  }
  tableStore.setCursor(items.cursor?.afterCursor);
  tableStore.replaceItems(items.data);
}, 400);

watch(
  () => [route.query.lat, route.query.lng],
  () => {
    void loadLocationsForMapCenter();
  },
);

watch(
  () => [itemList.value, route.query.spot],
  ([itemList, locationQuery]) => {
    if (!Array.isArray(itemList)) return;

    const locationId = locationQuery ? String(locationQuery) : null;

    const locationDto = itemList.find((loc) => loc !== null && (loc as BaseLocationDto).id === locationId);
    const locationIndex = itemList.findIndex((loc) => loc !== null && (loc as BaseLocationDto).id === locationId);

    if (!locationDto || !locationIndex) {
      return;
    }

    tableStore.setSelectedItem(locationDto as BaseLocationDto, locationIndex);
  },
);

const closeListView = () => {
  router.push({
    ...route,
    query: { ...route.query, side_panel: undefined },
  });
};

const handleZoomOut = () => {
  // @ts-ignore
  window.gMap?.map?.setZoom(12);
};

const updateSelectedLocation = async (location: BaseLocationDto): Promise<void> => {
  selectedLocationId.value = location.id;
  mapStore.setSelectedLocation(location.id);
  // @ts-ignore
  window.gMap?.map?.panTo(location.coordinates);
  // @ts-ignore
  window.gMap?.map?.setZoom(18);

  if (isMobile.value) {
    router.replace({ ...route, query: { ...route.query, side_panel: undefined } });
  }
};

onMounted(() => {
  const {
    query: { location },
  } = route;
  if (typeof location !== 'string') {
    return;
  }

  mapStore.setSelectedLocation(location);
});

onUnmounted(() => {
  tableStore.resetList(true);
  mapStore.setSelectedLocation(null);
});
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.locations-list-panel {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: var(--side-panel-form-width, 380px);
  z-index: 500;
  background: var(--color-dark-900);
  border-right: 1px solid var(--color-white-10);
  display: flex;
  flex-direction: column;
  pointer-events: auto;

  &.is-mobile {
    width: 100svw;
    border-right: none;
  }
}

.list-header {
  flex-shrink: 0;
}

.list-section-label {
  width: 100%;
  text-align: center;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--color-white-30);
  padding-top: var(--spacing-xl);
}

.list-tabs {
  display: flex;
  align-items: stretch;
  width: 100%;
  padding-top: var(--spacing-l);
}

.list-tab {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-l);
  cursor: pointer;
  padding: 0 32px var(--spacing-l);
  border-bottom: 1px solid var(--color-white-10);
  color: var(--color-white-30);
  transition:
    color var(--transition),
    border-color var(--transition);

  h5 {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    text-align: center;
    margin: 0;
  }

  &.active {
    color: var(--color-white-70);
    border-bottom-color: var(--color-white-100);
  }
  @include hover-capable {
    &:hover:not(.active) {
      color: var(--color-white-50);
    }
  }

  @media (max-width: 480px) {
    padding: 0 var(--spacing-l) var(--spacing-l);
  }
}

.list-scroll {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none;
  padding-bottom: 5rem;

  &::-webkit-scrollbar {
    display: none;
  }
}

.circles-grid {
  display: grid;
  grid-template-columns: repeat(3, 88px);
  justify-content: space-evenly;
  row-gap: var(--spacing-l);
  padding: var(--spacing-xl) 0 var(--spacing-l);
}

.skeleton-loader {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--spacing-l);
  padding: var(--spacing-l);
}

.skeleton-circle {
  width: 80px;
  height: 80px;
  border-radius: 50% !important;
  min-height: auto !important;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-md);
  margin-top: 4rem;
  padding: 0 var(--spacing-xl);
}

.empty-state-icon {
  opacity: 0.3;
}

.empty-state-label {
  font-size: 12px;
  line-height: 1.4;
  color: var(--color-white-40);
}

.zoom-out-cta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-s);
  padding: var(--spacing-xl) 0;
  font-size: 12px;
  font-weight: 500;
  color: var(--color-white-50);
  cursor: pointer;
  transition: color 150ms ease;

  @include hover {
    color: var(--color-white-80);
  }

  @include press;
}

.list-close-btn {
  position: absolute;
  bottom: var(--safe-bottom-offset);
  left: 50%;
  transform: translateX(-50%);
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 3.5rem;
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: var(--color-dark-5);
  color: var(--color-white-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 700;
  transition: all 200ms linear;

  @include hover {
    background-color: var(--color-dark-20);
    border-color: var(--color-white-30);
    color: var(--color-white);
  }

  @include press($transform: translateX(-50%));
}

.list-slide-enter-active,
.list-slide-leave-active {
  transition: transform 300ms ease;
}

.list-slide-enter-from,
.list-slide-leave-to {
  transform: translateX(-100%);
}
</style>
